import * as Three from 'three';
import { iconsPath } from '../../utils/imagePath';
import { update_assets_data } from '../../redux/Slice/gridSlice'; // Import your Redux action
import { Publish } from '../../pages/Dashboard/GridPage'; // Import the Publish function
import { handleImageUpload } from './image'; // Import handleImageUpload for image files
import { handleMeshUpload } from './Mesh'; // Import handleMeshUpload for mesh files
import { handleMeshObjUpload } from './MeshObj';
import { handleMeshFbxUpload } from './MeshFbx';
import { handleVideoUpload } from './Video'; // Import handleVideoUpload for video files
import { handleAudioUpload } from './Audio'; // Import handleAudioUpload for audio files
import toast from 'react-hot-toast';
import { handleMeshPlyUpload } from './MeshPly';
import { SplatLoader } from '@sparkjsdev/spark';

export function enableDragDrop(
  scene,
  camera,
  renderer,
  transformControls,
  store,
  DRACOLoader,
  GLTFLoader
) {
  const gltfLoader = new GLTFLoader();
  const textureLoader = new Three.TextureLoader();
  const raycaster = new Three.Raycaster();
  const mouse = new Three.Vector2();

  // Store objects that can be picked later
  const selectableObjects = [];

  // Helper function to generate unique ID
  const generateId = () =>
    `asset_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Helper function to add asset to Redux store
  const addAssetToStore = (assetData, assetType) => {
    const currentState = store.getState();
    const currentAssets =
      currentState.assets_object.assets_data[assetType] || [];

    const newAssets = [...currentAssets, assetData];
    store.dispatch(update_assets_data({ type: assetType, data: newAssets }));
  };

  // Call Publish after adding asset to the store
  const publishAsset = (assetData) => {
    const setPublished = (status) => console.log('Publish status:', status);
    const isSave = true;
    const setName = (name) => console.log('Set name:', name);
    const completeObject = assetData;
    const timelineEffects = null;
    const cleanallanimation = null;

    Publish(
      setPublished,
      isSave,
      setName,
      completeObject,
      timelineEffects,
      cleanallanimation
    );
  };

  // Invisible ground plane
  const groundGeometry = new Three.PlaneGeometry(1000, 1000);
  const groundMaterial = new Three.MeshBasicMaterial({ visible: false });
  const ground = new Three.Mesh(groundGeometry, groundMaterial);
  ground.rotateX(-Math.PI / 2);
  scene.add(ground);

  // Prevent browser default
  renderer.domElement.addEventListener('dragover', (event) => {
    event.preventDefault();
  });

  const readDroppedEntry = (entry) =>
    new Promise((resolve) => {
      if (!entry) {
        resolve([]);
        return;
      }

      if (entry.isFile) {
        entry.file(
          (file) => resolve(file ? [file] : []),
          () => resolve([])
        );
        return;
      }

      if (!entry.isDirectory) {
        resolve([]);
        return;
      }

      const dirReader = entry.createReader();
      const entries = [];

      const readBatch = () => {
        dirReader.readEntries(
          (results) => {
            if (!results || results.length === 0) {
              Promise.all(entries.map((child) => readDroppedEntry(child)))
                .then((nested) => resolve(nested.flat()))
                .catch(() => resolve([]));
              return;
            }
            entries.push(...results);
            readBatch();
          },
          () => resolve([])
        );
      };

      readBatch();
    });

  const collectDroppedFiles = async (dataTransfer) => {
    const fallback = Array.from(dataTransfer?.files || []);
    const items = Array.from(dataTransfer?.items || []);

    if (!items.length) return fallback;

    const entries = items
      .map((item) =>
        typeof item.webkitGetAsEntry === 'function'
          ? item.webkitGetAsEntry()
          : null
      )
      .filter(Boolean);

    if (!entries.length) return fallback;

    try {
      const nested = await Promise.all(entries.map((entry) => readDroppedEntry(entry)));
      const flattened = nested.flat().filter(Boolean);
      return flattened.length ? flattened : fallback;
    } catch {
      return fallback;
    }
  };

  // Handle file drop
  renderer.domElement.addEventListener('drop', async (event) => {
    event.preventDefault();

    const allFiles = await collectDroppedFiles(event.dataTransfer);
    const file = allFiles[0];
    if (!file) return;

    // Mouse position → NDC
    mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
    mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

    // Raycast to ground
    raycaster.setFromCamera(mouse, camera);
    const intersects = raycaster.intersectObject(ground);
    const dropPosition =
      intersects.length > 0 ? intersects[0].point : new Three.Vector3(0, 0, 0);

    const droppedObjFile = allFiles.find((f) =>
      f?.name?.toLowerCase?.().endsWith('.obj')
    );
    const droppedFbxFile = allFiles.find((f) =>
      f?.name?.toLowerCase?.().endsWith('.fbx')
    );

    // Delegate to handleMeshObjUpload for OBJ folder/single-file drops
    if (droppedObjFile) {
      handleMeshObjUpload(
        { target: { files: allFiles } },
        {
          DRACOLoader,
          scene,
          transformControls,
          selectedObject: null,
          setPolyCount: () => {},
          renderer,
          GLTFLoader,
        },
        (pendingUploads) => {}
      );
      return;
    }

    // Delegate to handleMeshFbxUpload for FBX folder/single-file drops
    if (droppedFbxFile) {
      handleMeshFbxUpload(
        { target: { files: allFiles } },
        {
          scene,
          transformControls,
          selectedObject: null,
          setPolyCount: () => {},
          renderer,
          GLTFLoader,
          DRACOLoader,
        },
        (pendingUploads) => {}
      );
      return;
    }

    // Delegate to handleImageUpload for image files
    if (/\.(jpg|jpeg|png)$/i.test(file.name)) {
      handleImageUpload(
        { target: { files: [file] } },
        {
          Three: Three,
          scene,
          transformControls,
          selectedObject: null,
          setPolyCyount: () => {},
        },
        (pendingUploads) => {}
      );
      return;
    }

    // Delegate to handleMeshUpload for mesh files
    if (
      file.name.toLowerCase().endsWith('.glb') ||
      file.name.toLowerCase().endsWith('.gltf')
    ) {
      handleMeshUpload(
        { target: { files: [file] } },
        {
          GLTFLoader,
          DRACOLoader,
          scene,
          transformControls,
          selectedObject: null,
          setPolyCount: () => {},
        },
        (pendingUploads) => {}
      );
      return;
    }

    // Delegate to handleVideoUpload for video files
    if (file.name.toLowerCase().endsWith('.mp4')) {
      handleVideoUpload(
        { target: { files: [file] } },
        {
          Three,
          scene,
          transformControls,
          selectedObject: null,
          setPolyCount: () => {},
        },
        (pendingUploads) => {}
      );
      return;
    }

    // Delegate to handleAudioUpload for audio files
    if (file.name.toLowerCase().endsWith('.mp3')) {
      handleAudioUpload(
        { target: { files: [file] } },
        {
          Three,
          scene,
          transformControls,
          camera,
          selectedObject: null,
          setPolyCount: () => {},
        },
        (pendingUploads) => {}
      );
      return;
    }

    if (file.name.toLowerCase().endsWith('.ply')) {
      handleMeshPlyUpload(
        { target: { files: [file] } },
        {
          Three,
          SplatLoader,
          scene,
          transformControls,
          selectedObject: null,
          setPolyCount: () => {},
        },
        (pendingUploads) => {}
      );
      return;
    }

    // Handle other file types (GLB, MP4, etc.)
    const reader = new FileReader();
    const assetId = generateId();

    if (
      file.name.toLowerCase().endsWith('.glb') ||
      file.name.toLowerCase().endsWith('.gltf')
    ) {
      reader.onload = (e) => {
        const arrayBuffer = e.target.result;
        gltfLoader.parse(arrayBuffer, '', (gltf) => {
          const model = gltf.scene;
          model.position.copy(dropPosition);
          model.name = assetId; // Set name for scene lookup
          scene.add(model);
        });
      };
      reader.readAsArrayBuffer(file);
    }

    // Images
    else if (/\.(jpg|jpeg|png)$/i.test(file.name)) {
      reader.onload = (e) => {
        const texture = textureLoader.load(e.target.result);
        const material = new Three.MeshBasicMaterial({
          map: texture,
          side: Three.DoubleSide,
        });
        const geometry = new Three.PlaneGeometry(0.5, 0.5);
        const mesh = new Three.Mesh(geometry, material);
        mesh.position.copy(dropPosition);
        mesh.name = assetId; // Set name for scene lookup
        scene.add(mesh);

        // Add to Redux store
        const assetData = {
          name: assetId,
          file_name: file.name,
          asset_file_name: file.name,
          url: assetId,
          temp_file_name: file.name,
          position: [dropPosition.x, dropPosition.y, dropPosition.z],
          rotation: [0, 0, 0],
          scale: [1, 1, 1],
          material_settings: {
            color: '#ffffff',
            opacity: 1,
            transparent: false,
          },
        };
        addAssetToStore(assetData, 'image');
        publishAsset(assetData); // Trigger publish

        selectableObjects.push(mesh);
        if (transformControls) transformControls.attach(mesh);
      };
      reader.readAsDataURL(file);
    }

    // Video (MP4)
    else if (/\.(mp4)$/i.test(file.name)) {
      reader.onload = (e) => {
        const video = document.createElement('video');
        video.src = e.target.result;
        video.loop = true;
        video.muted = true;
        video.play();

        const videoTexture = new Three.VideoTexture(video);
        const material = new Three.MeshBasicMaterial({
          map: videoTexture,
          side: Three.DoubleSide,
        });
        const geometry = new Three.PlaneGeometry(0.8, 0.45);

        const mesh = new Three.Mesh(geometry, material);
        mesh.position.copy(dropPosition);
        mesh.name = assetId; // Set name for scene lookup
        scene.add(mesh);

        // Add to Redux store
        const assetData = {
          name: assetId,
          file_name: file.name,
          asset_file_name: file.name,
          url: assetId,
          temp_file_name: file.name,
          position: [dropPosition.x, dropPosition.y, dropPosition.z],
          rotation: [0, 0, 0],
          scale: [1, 1, 1],
          video_settings: {
            loop: true,
            muted: true,
            autoplay: true,
            volume: 1,
          },
        };
        addAssetToStore(assetData, 'video');
        publishAsset(assetData); // Trigger publish

        selectableObjects.push(mesh);
        if (transformControls) transformControls.attach(mesh);
      };
      reader.readAsDataURL(file);
    }

    // Audio (MP3)
    else if (/\.(mp3)$/i.test(file.name)) {
      reader.onload = (e) => {
        const listener = new Three.AudioListener();
        camera.add(listener);

        const sound = new Three.Audio(listener);
        const audioLoader = new Three.AudioLoader();
        audioLoader.load(e.target.result, (buffer) => {
          sound.setBuffer(buffer);
          sound.setLoop(true);
          sound.setVolume(0.5);
          sound.play();
        });

        // Custom icon
        const iconTexture = new Three.TextureLoader().load(iconsPath.AudioIcon);
        const material = new Three.MeshBasicMaterial({
          map: iconTexture,
          transparent: true,
          side: Three.DoubleSide,
        });
        const geometry = new Three.PlaneGeometry(0.7, 0.7);
        const iconMesh = new Three.Mesh(geometry, material);
        iconMesh.position.copy(dropPosition);
        iconMesh.name = assetId; // Set name for scene lookup
        scene.add(iconMesh);

        // Add to Redux store
        const assetData = {
          name: assetId,
          file_name: file.name,
          asset_file_name: file.name,
          url: assetId,
          temp_file_name: file.name,
          position: [dropPosition.x, dropPosition.y, dropPosition.z],
          rotation: [0, 0, 0],
          scale: [1, 1, 1],
          audio_settings: {
            loop: true,
            volume: 0.5,
            autoplay: true,
          },
        };
        addAssetToStore(assetData, 'audio');
        publishAsset(assetData); // Trigger publish

        selectableObjects.push(iconMesh);
        if (transformControls) transformControls.attach(iconMesh);

        iconMesh.userData.sound = sound;
      };
      reader.readAsDataURL(file);
    } else {
      const CustomCloseButton = ({ closeToast }) => (
        <button
          onClick={closeToast}
          className="absolute -top-2 -right-2 text-white hover:text-white text-lg font-bold leading-none w-6 h-6 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
          style={{ zIndex: 10 }}
        >
          ✕
        </button>
      );

      toast.error(
        <div className="relative flex flex-col pr-8">
          {/* Title with warning icon */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-yellow-400 text-lg">⚠️</span>
            <span className="font-semibold text-white text-base">
              Unsupported File Format
            </span>
          </div>
          {/* Description */}
          <p className="text-sm text-white/50 leading-relaxed mb-3">
            Supported types: <b>GLB, FBX, OBJ, PLY, JPG, PNG, MP4, MP3</b>
          </p>
        </div>,
        {
          style: {
            background: '#121316',
            borderRadius: '10px',
            padding: '10px 14px',
            color: '#fff',
            position: 'relative',
          },
          icon: false,
          position: 'top-center',
          closeButton: false,
          hideProgressBar: true,
        }
      );
    }
  });

  // Add click selection with scene-to-panel sync
  // Note: selectObjectInScene is injected from GridPage.jsx
  renderer.domElement.addEventListener('pointerdown', (event) => {
    if (store.getState()?.window?.window_type === 'preview') return;
    // Primary pointer: mouse LMB, trackpad click, or single-finger touch/pen.
    const isTouchLike =
      event.pointerType === 'touch' || event.pointerType === 'pen';
    if (isTouchLike) {
      if (event.isPrimary === false) return;
    } else if (event.button !== 0 && event.buttons !== 1) {
      return;
    }
    // GridPage handleMouseDown handles selection (including Ctrl+multi-select)
    if (event.ctrlKey || event.metaKey) return;

    const rect = renderer.domElement.getBoundingClientRect();
    const width = rect.width || 1;
    const height = rect.height || 1;
    mouse.x = ((event.clientX - rect.left) / width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / height) * 2 + 1;

    raycaster.setFromCamera(mouse, camera);
    const intersects = raycaster.intersectObjects(selectableObjects, true);

    if (intersects.length > 0) {
      const picked = intersects[0].object;
      // Dispatch selection to Redux if selectObjectInScene is available
      // This will sync the scene selection with left panel and clamp to
      // particle/GLB roots (never attach transform to an inner cloud sphere).
      if (
        window.__selectObjectInScene &&
        typeof window.__selectObjectInScene === 'function'
      ) {
        window.__selectObjectInScene(picked);
      } else if (transformControls) {
        transformControls.attach(picked);
      }
    }
  });
}
